import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import {
  Brain, ChevronRight, Clock, CheckCircle2, XCircle,
  Plus, RefreshCw, X, Inbox,
} from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────

interface SessionCard {
  id: string;
  org_id: string;
  org_name: string;
  trigger_type: string;
  status: string;
  recommendation_preview: string;
  created_at: string;
  approved_at: string | null;
  sam_edits_count: number;
}

interface OrgRow {
  org_id: string;
  org_name: string;
  created_at: string;
  knowledge_store_complete: boolean;
}

// ── Constants ────────────────────────────────────────────────────

const TRIGGER_OPTIONS = [
  { value: 'manual',                label: 'Manual / Ad-hoc' },
  { value: 'weekly_recommendation', label: 'Weekly Recommendation' },
  { value: 'user_at_risk',          label: 'User at Risk' },
  { value: 'experiment_needed',     label: 'Experiment Needed' },
  { value: 'health_flag_high',      label: 'Health Flag — High Severity' },
  { value: 'knowledge_incomplete',  label: 'Knowledge Not Set Up' },
  { value: 'comms_gap_14d',         label: 'Comms Gap (14 days)' },
];

const TRIGGER_LABELS: Record<string, string> = Object.fromEntries(
  TRIGGER_OPTIONS.map(o => [o.value, o.label])
);

function parseRecommendationPreview(thinkingText: string): string {
  try {
    const p = JSON.parse(thinkingText);
    const text: string = p.recommendation ?? '';
    return text.length > 120 ? text.slice(0, 117) + '…' : text;
  } catch {
    return thinkingText.slice(0, 120);
  }
}

function placeholderThinkingText(orgName: string, triggerType: string): string {
  const triggerLabel = TRIGGER_LABELS[triggerType] ?? triggerType;
  return JSON.stringify({
    recommendation: `[Edit — describe what Cosmo should recommend for ${orgName} given the "${triggerLabel}" trigger]`,
    reasoning: '[Edit — explain why this is the right move based on what you know about this org]',
    approach: '[Edit — describe how you\'d execute the recommendation]',
    assumptions: ['[Add assumptions here — one per line]'],
    outputs_if_approved: ['[List what should be produced when approved]'],
  });
}

// ── Main component ───────────────────────────────────────────────

export default function ThinkingLayerIndex() {
  const navigate = useNavigate();

  const [sessions, setSessions]   = useState<SessionCard[]>([]);
  const [orgs, setOrgs]           = useState<OrgRow[]>([]);
  // Start checking = true immediately — user sees scanning state before any data loads
  const [checking, setChecking]   = useState(true);

  // Manual panel state
  const [showManual, setShowManual]       = useState(false);
  const [manualOrgId, setManualOrgId]     = useState('');
  const [manualTrigger, setManualTrigger] = useState('manual');
  const [manualNote, setManualNote]       = useState('');
  const [manualBusy, setManualBusy]       = useState(false);

  // ── Session creation + AI call ────────────────────────────────

  const generateAutoSession = useCallback(async (
    orgId: string,
    orgName: string,
    triggerType: string,
    contextNote: string,
  ) => {
    const { data: session, error } = await supabase
      .from('cosmo_thinking_sessions')
      .insert({
        org_id: orgId,
        trigger_type: triggerType,
        thinking_text: placeholderThinkingText(orgName, triggerType),
        status: 'draft',
      })
      .select()
      .single();

    if (error) {
      console.error('Failed to insert thinking session:', error);
      return;
    }

    // Call AI — silently fall back if edge function not yet deployed
    const { error: fnErr } = await supabase.functions.invoke('generate-thinking', {
      body: {
        org_id: orgId,
        thinking_session_id: session.id,
        trigger_type: triggerType,
        context_note: contextNote,
      },
    });

    if (fnErr) {
      console.info('generate-thinking not deployed; placeholder used for', orgName);
    }
  }, []);

  // ── Core load-and-check function ──────────────────────────────
  // This runs as one atomic flow: fetch data → scan triggers → update sessions.
  // `checking` stays true for the full duration so the UI shows the scanning
  // banner from the very first render through to completion.

  const loadAndCheck = useCallback(async () => {
    setChecking(true);

    // 1. Fetch sessions + orgs in parallel
    const [sessionRes, orgRes, recentSessionsRes] = await Promise.all([
      supabase
        .from('cosmo_thinking_sessions')
        .select('id, org_id, trigger_type, status, thinking_text, sam_edits, created_at, approved_at')
        .order('created_at', { ascending: false })
        .limit(100),
      supabase
        .from('organizations')
        .select('id, name, created_at, knowledge_store_complete')
        .order('name'),
      supabase
        .from('cosmo_thinking_sessions')
        .select('org_id, trigger_type, created_at')
        .gt('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()),
    ]);

    // 2. Build derived data from fresh results (no stale state reads)
    const orgData: OrgRow[] = (orgRes.data ?? []).map(o => ({
      org_id: o.id,
      org_name: o.name,
      created_at: o.created_at,
      knowledge_store_complete: o.knowledge_store_complete,
    }));
    const orgMap = Object.fromEntries(orgData.map(o => [o.org_id, o.org_name]));

    const sessionData: SessionCard[] = (sessionRes.data ?? []).map(s => ({
      id: s.id,
      org_id: s.org_id,
      org_name: orgMap[s.org_id] ?? s.org_id,
      trigger_type: s.trigger_type,
      status: s.status,
      recommendation_preview: parseRecommendationPreview(s.thinking_text),
      created_at: s.created_at,
      approved_at: s.approved_at,
      sam_edits_count: Array.isArray(s.sam_edits) ? s.sam_edits.length : 0,
    }));

    // Populate state for UI and for manual-session form
    setOrgs(orgData);
    setSessions(sessionData);

    // 3. Dedup set: skip orgs+triggers that already have a recent session
    const recentSet = new Set<string>(
      (recentSessionsRes.data ?? []).map(s => `${s.org_id}::${s.trigger_type}`)
    );

    let generated = 0;
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

    for (const org of orgData) {
      // Trigger: unresolved high-severity health flag
      const keyFlag = `${org.org_id}::health_flag_high`;
      if (!recentSet.has(keyFlag)) {
        const { data: flags } = await supabase
          .from('org_health_flags')
          .select('id')
          .eq('org_id', org.org_id)
          .eq('severity', 'high')
          .is('resolved_at', null)
          .limit(1);

        if (flags && flags.length > 0) {
          await generateAutoSession(
            org.org_id,
            org.org_name,
            'health_flag_high',
            `There is an unresolved high-severity issue flagged for ${org.org_name}. Recommend how to address it.`,
          );
          recentSet.add(keyFlag);
          generated++;
        }
      }

      // Trigger: knowledge store incomplete after 7+ days
      const keyKnowledge = `${org.org_id}::knowledge_incomplete`;
      if (!org.knowledge_store_complete && !recentSet.has(keyKnowledge)) {
        const orgAgeMs = Date.now() - new Date(org.created_at).getTime();
        if (orgAgeMs > sevenDaysMs) {
          await generateAutoSession(
            org.org_id,
            org.org_name,
            'knowledge_incomplete',
            `The knowledge store for ${org.org_name} has not been completed and the org was created more than 7 days ago. Recommend how to prompt Sam to set it up and what value it would unlock.`,
          );
          recentSet.add(keyKnowledge);
          generated++;
        }
      }

      // TODO: comms_gap_14d trigger once council_comms_packages table is available
    }

    // 4. If new sessions were created, reload session list so inbox is fresh
    if (generated > 0) {
      toast.success(`Cosmo generated ${generated} new thinking session${generated > 1 ? 's' : ''}`);
      const { data: freshSessions } = await supabase
        .from('cosmo_thinking_sessions')
        .select('id, org_id, trigger_type, status, thinking_text, sam_edits, created_at, approved_at')
        .order('created_at', { ascending: false })
        .limit(100);
      setSessions(
        (freshSessions ?? []).map(s => ({
          id: s.id,
          org_id: s.org_id,
          org_name: orgMap[s.org_id] ?? s.org_id,
          trigger_type: s.trigger_type,
          status: s.status,
          recommendation_preview: parseRecommendationPreview(s.thinking_text),
          created_at: s.created_at,
          approved_at: s.approved_at,
          sam_edits_count: Array.isArray(s.sam_edits) ? s.sam_edits.length : 0,
        }))
      );
    }

    setChecking(false);
  }, [generateAutoSession]);

  // Run once on mount
  useEffect(() => {
    loadAndCheck();
  }, [loadAndCheck]);

  // ── Manual session submission ────────────────────────────────

  const handleManualSubmit = async () => {
    if (!manualOrgId) { toast.error('Select an org first'); return; }
    setManualBusy(true);
    const org = orgs.find(o => o.org_id === manualOrgId);
    await generateAutoSession(
      manualOrgId,
      org?.org_name ?? manualOrgId,
      manualTrigger,
      manualNote,
    );
    // Reload just the sessions list (no need to re-run all checks)
    const orgMap = Object.fromEntries(orgs.map(o => [o.org_id, o.org_name]));
    const { data: freshSessions } = await supabase
      .from('cosmo_thinking_sessions')
      .select('id, org_id, trigger_type, status, thinking_text, sam_edits, created_at, approved_at')
      .order('created_at', { ascending: false })
      .limit(100);
    setSessions(
      (freshSessions ?? []).map(s => ({
        id: s.id,
        org_id: s.org_id,
        org_name: orgMap[s.org_id] ?? s.org_id,
        trigger_type: s.trigger_type,
        status: s.status,
        recommendation_preview: parseRecommendationPreview(s.thinking_text),
        created_at: s.created_at,
        approved_at: s.approved_at,
        sam_edits_count: Array.isArray(s.sam_edits) ? s.sam_edits.length : 0,
      }))
    );
    setManualBusy(false);
    setShowManual(false);
    setManualOrgId('');
    setManualNote('');
    setManualTrigger('manual');
    toast.success('Thinking session created');
  };

  // ── Derived data ─────────────────────────────────────────────

  const pending  = sessions.filter(s => s.status === 'draft');
  const reviewed = sessions.filter(s => s.status !== 'draft');

  const triggerLabel = (type: string) => TRIGGER_LABELS[type] ?? type.replace(/_/g, ' ');

  // ── Render helpers ───────────────────────────────────────────

  const StatusBadge = ({ status }: { status: string }) => {
    if (status === 'approved') return (
      <Badge variant="outline" className="text-xs gap-1 border-green-500/30 text-green-600 dark:text-green-400">
        <CheckCircle2 className="h-3 w-3" />Approved
      </Badge>
    );
    if (status === 'rejected') return (
      <Badge variant="outline" className="text-xs gap-1 border-red-500/30 text-red-600 dark:text-red-400">
        <XCircle className="h-3 w-3" />Rejected
      </Badge>
    );
    return (
      <Badge variant="outline" className="text-xs gap-1 border-yellow-500/30 text-yellow-600 dark:text-yellow-400">
        <Clock className="h-3 w-3" />Pending Review
      </Badge>
    );
  };

  const SessionRow = ({ s, compact = false }: { s: SessionCard; compact?: boolean }) => (
    <button
      onClick={() => navigate(`/admin/growth/thinking/${s.org_id}?session=${s.id}`)}
      className="w-full text-left border rounded-xl px-5 py-4 hover:bg-muted/50 transition-colors group"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">{s.org_name}</span>
            <span className="text-muted-foreground text-sm">·</span>
            <span className="text-sm text-muted-foreground">{triggerLabel(s.trigger_type)}</span>
            {s.sam_edits_count > 0 && (
              <span className="text-xs text-muted-foreground/60">
                · {s.sam_edits_count} edit{s.sam_edits_count > 1 ? 's' : ''}
              </span>
            )}
          </div>
          {!compact && s.recommendation_preview && (
            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
              "{s.recommendation_preview}"
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            {s.status === 'draft'
              ? `Generated ${formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}`
              : `Reviewed ${formatDistanceToNow(new Date(s.approved_at ?? s.created_at), { addSuffix: true })}`}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <StatusBadge status={s.status} />
          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </button>
  );

  // ── Main render ──────────────────────────────────────────────

  return (
    <div className="max-w-3xl mx-auto space-y-6 py-4">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Brain className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Cosmo's Inbox</h1>
            <p className="text-sm text-muted-foreground">
              Cosmo surfaces thinking sessions automatically. You review, edit, and approve.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={loadAndCheck}
            disabled={checking}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Scanning…' : 'Scan now'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowManual(v => !v)}
            disabled={checking}
          >
            {showManual ? <X className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
            {showManual ? 'Cancel' : 'Start manually'}
          </Button>
        </div>
      </div>

      {/* Scanning banner — visible from first render through check completion */}
      {checking && (
        <div className="flex items-center gap-3 px-4 py-3 bg-muted/40 border rounded-xl text-sm text-muted-foreground">
          <Brain className="h-4 w-4 text-primary animate-pulse shrink-0" />
          <span>Cosmo is checking your orgs for trigger conditions…</span>
        </div>
      )}

      {/* Manual panel */}
      {showManual && !checking && (
        <div className="border rounded-xl p-5 space-y-4 bg-muted/20">
          <p className="text-sm font-medium">Start a thinking session manually</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Organization</Label>
              <Select value={manualOrgId} onValueChange={setManualOrgId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select org…" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map(o => (
                    <SelectItem key={o.org_id} value={o.org_id}>{o.org_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Trigger type</Label>
              <Select value={manualTrigger} onValueChange={setManualTrigger}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              Context for Cosmo{' '}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              value={manualNote}
              onChange={e => setManualNote(e.target.value)}
              placeholder="e.g. Tom Ellis hasn't logged in for 10 days. I want to re-engage him before the expansion call."
              rows={2}
            />
          </div>
          <Button
            size="sm"
            onClick={handleManualSubmit}
            disabled={manualBusy || !manualOrgId}
          >
            <Brain className="h-4 w-4 mr-2" />
            {manualBusy ? 'Generating…' : 'Generate Thinking'}
          </Button>
        </div>
      )}

      {/* Empty state — only shown after check completes */}
      {!checking && pending.length === 0 && reviewed.length === 0 && (
        <div className="border border-dashed rounded-xl py-14 text-center space-y-3">
          <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mx-auto">
            <Inbox className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium">Inbox is clear</p>
          <p className="text-xs text-muted-foreground">
            No active orgs triggered any conditions right now.
          </p>
          <Button variant="outline" size="sm" onClick={() => setShowManual(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Start manually
          </Button>
        </div>
      )}

      {/* Pending Review queue */}
      {pending.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Pending Review
            </p>
            <span className="h-5 min-w-5 px-1.5 flex items-center justify-center rounded-full bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 text-xs font-semibold">
              {pending.length}
            </span>
          </div>
          <div className="space-y-2">
            {pending.map(s => <SessionRow key={s.id} s={s} />)}
          </div>
        </section>
      )}

      {/* Reviewed queue */}
      {reviewed.length > 0 && (
        <section className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Reviewed
          </p>
          <div className="space-y-2">
            {reviewed.map(s => <SessionRow key={s.id} s={s} compact />)}
          </div>
        </section>
      )}
    </div>
  );
}
